import { useCallback } from 'react';
import { QueryModel, SearchModel } from '../DataTableQuery.types';
import { usePluginContext } from '../../../plugins/impl';
import { QueryVariablePreprocessor } from './initialQueryVariableProcessors.types';

export function useFilterProcessor(filterIds: string[] | null): QueryVariablePreprocessor.Function {
  const hasFilters = usePluginContext().isDestinationDefined('filters');

  return useCallback(
    (prevResult) => {
      const result = prevResult?.result ?? null;
      const isEmptyModel = isEmptyObject(result?.query) && isEmptyArray(result?.sort);

      /*
       * If filters are not present, we assume that the query is not relevant to the filters.
       * If the query is empty, we pass the query through.
       */
      if (!hasFilters || isEmptyModel) {
        return {
          result,
          status: QueryVariablePreprocessor.Status.COMPLETED
        };
      }

      /*
       * If there are any filters, we wait for them.
       */
      if (!filterIds?.length) {
        return {
          result,
          status: QueryVariablePreprocessor.Status.IN_PROGRESS
        };
      }
      return {
        result: {
          ...result,
          // remove query params that are not reflected in hub filters
          query: Object.entries(result?.query ?? {}).reduce<QueryModel>((acc, [key, value]) => {
            if (![...filterIds, 'search'].includes(key)) {
              return acc;
            }
            return {
              ...acc,
              [key]: value
            };
          }, {})
        } as SearchModel<any, any>,
        status: QueryVariablePreprocessor.Status.COMPLETED
      };
    },
    [filterIds, hasFilters]
  );
}

const isEmptyObject = (obj: any) => {
  if (obj === null || obj === undefined) {
    return true;
  }
  return Object.keys(obj).length === 0;
};

const isEmptyArray = (arr: any) => {
  if (arr === null || arr === undefined) {
    return true;
  }
  return Array.isArray(arr) && arr.length === 0;
};
